import React, { useReducer, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { useManagedMutation } from 'utils/managedHooks';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery, useMutation } from '@apollo/client';
import { isEmpty } from 'ramda';
import { useForm } from 'react-hook-form';

import config from 'utils/config';
import { hasDog } from 'utils/booking';

import {
  CONFIRM_BOOKING,
  ADD_ADDITIONAL_DETAILS,
  UPDATE_ADDITIONAL_DETAILS,
  DELETE_ADDITIONAL_DETAILS,
  CANCEL_SPECIFIC_BOOKING_ITEMS,
  ADD_ASSISTANCE_TO_BOOKING,
  IMPORT_HOTEL_BOOKING,
  ADD_EXTERNAL_REFERENCE
} from 'store/mutations';
import { GET_HOTEL_BOOKING_INFO, HOTEL_BOOKING } from 'store/queries';
import { ADD_HOTEL_PRODUCT } from 'pages/BookingDetails/Extras/ExtrasHotel/AddHotel/graphql';
import { INBOUND, OUTBOUND, BOTH } from 'constants/booking';
import { ASSISTANCE_KEYS } from 'constants/assistance';
import { passengersShape, legShape, feeShape } from 'constants/propTypes';
import { buildHotelInfo, buildHotelInfoFromDotwBooking } from 'utils/hotel';
import { EXTERNAL_REFERENCES } from 'constants/booking';

import { StyledGenericMessage, Container, StyledHeader, StyledMessage } from '../AddAssistance.styles';
import NoAssistanceResults from '../NoAssistanceResults';

import AddHotelForm from './AddHotelForm';
import AddAssistanceOptions from './AddAssistanceOptions';
import { reducer, getInitialState } from './AddAssistanceForm.reducer';
import {
  onToggleAssistanceVisibilityAction,
  onRemoveAllItemsAction,
  onRemoveItemAction,
  onAddItemAction,
  onEditItemAction
} from './AddAssistanceForm.actions';
import {
  getErrorKey,
  buildAdditionalDetails,
  buildAssistanceAdditionalDetails,
  buildAssistanceUpdatedDetails,
  getExtrasLimitExceededDirections
} from './utils';

const buildJourneyOption = (id) => ({ id, translationId: `addAssistance.journey.${id}` });

const AddAssistanceForm = (props) => {
  const {
    assistanceOptions,
    outboundExtrasOptions,
    inboundExtrasOptions,
    outboundExtras,
    inboundExtras,
    bookingReference,
    refetchBooking,
    isReturn,
    onNext,
    passengers,
    initialState,
    currency,
    isPackageBooking,
    hasActiveHotel,
    setLoading,
    setIsConfirmDisabled,
    shouldConfirmBooking,
    isEurostarOrganization = false,
    isGroupBooking = false,
    showAssistance = false,
    showExtras = false,
    showHotel = false
  } = props;

  const history = useHistory();
  const paxIdHeterogenous = new URLSearchParams(history.location?.search).get('passengerId');

  const isBookingPathExtrasEnabled = config('ENABLE_EXTRAS_BOOKING_PATH', { canOverride: true }) === 'true';

  const { formState, register, unregister, handleSubmit, watch, setValue } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;

  const hasDogPassenger = hasDog({ passengers });
  const jacTravelRef = watch('jacTravelRef');

  const hasErrors = Boolean(Object.keys(errors).length > 0);

  const [hotelInfo, setHotelInfo] = useState(null);
  const [exceededLimits, setExceededLimits] = useState({
    outbound: false,
    inbound: false,
    outboundTotal: 0,
    inboundTotal: 0
  });

  const extrasOptions = [...(outboundExtrasOptions || []), ...(inboundExtrasOptions || [])];
  const visibleOptions = [...(showAssistance ? assistanceOptions : []), ...(showExtras ? extrasOptions : [])];
  const sortedAssistanceOptions = [...assistanceOptions].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));

  const [state, dispatch] = useReducer(
    reducer,
    initialState || getInitialState(outboundExtras, inboundExtras, visibleOptions, passengers, isReturn, paxIdHeterogenous)
  );

  const [confirmBooking, { loading: confirmBookingLoading }] = useManagedMutation(CONFIRM_BOOKING);

  const [addHotelProduct, { loading: addHotelLoading }] = useManagedMutation(ADD_HOTEL_PRODUCT);

  const [addAssistanceToBooking, { loading: addAssistanceLoading }] = useManagedMutation(ADD_ASSISTANCE_TO_BOOKING);

  const [cancelSpecificBookingItems, { loading: removeAssistanceLoading }] = useManagedMutation(CANCEL_SPECIFIC_BOOKING_ITEMS);

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useManagedMutation(ADD_ADDITIONAL_DETAILS);

  const [updateAdditionalDetails, { loading: updateAdditionalDetailsLoading }] = useManagedMutation(UPDATE_ADDITIONAL_DETAILS);

  const [deleteAdditionalDetails, { loading: deleteAdditionalDetailsLoading }] = useManagedMutation(DELETE_ADDITIONAL_DETAILS);

  const [addExternalReference, { loading: addExternalReferenceLoading }] = useManagedMutation(ADD_EXTERNAL_REFERENCE);

  const [getHotelBookingInfo, { loading: hotelLoading, called: getHotelCalled }] = useLazyQuery(GET_HOTEL_BOOKING_INFO, {
    onCompleted: (data) => data?.getHotelBooking && setHotelInfo(buildHotelInfo(data?.getHotelBooking)),
    fetchPolicy: 'network-only'
  });

  const updateHotelForm = (data) => {
    setHotelInfo(buildHotelInfoFromDotwBooking(data));
    unregister(['hotelId', 'eurostarPackageRef']);
    setValue('hotelId', data?.hotel?.id, { shouldValidate: true });
    setValue('eurostarPackageRef', bookingReference, { shouldValidate: true });
  };

  const [getHotelBooking, { loading: hotelBookingLoading }] = useLazyQuery(HOTEL_BOOKING, {
    onCompleted: (data) => data?.hotelBooking && updateHotelForm(data?.hotelBooking),
    fetchPolicy: 'network-only'
  });

  const [importHotelBooking, { loading: importHotelBookingLoading }] = useMutation(IMPORT_HOTEL_BOOKING, {
    onCompleted: (data) => data.importHotelBooking && updateHotelForm(data.importHotelBooking),
    onError: async (error) => {
      const isAlreadBeenImported = error.graphQLErrors?.find(
        ({ extensions }) => extensions?.code === 'HOTEL_BOOKING_ERROR_BOOKING_ALREADY_EXISTS'
      );

      if (isAlreadBeenImported) {
        await getHotelBooking({ variables: { reference: bookingReference } });
      }
    }
  });

  const isGeneralWarningVisible = Boolean(state && state.isAssistanceIncomplete);
  const shouldRefetchHotel = jacTravelRef !== hotelInfo?.bookingReference && hotelInfo?.isDotW === false;

  const hotelFormError = getErrorKey({ errors, hotelInfo, hotelLoading, getHotelCalled, currency, hasErrors });

  const isHotelFormInvalid = !formState.isValid || !!hotelFormError || shouldRefetchHotel;

  let isInboundOnly = false;

  if (!isReturn) {
    isInboundOnly = assistanceOptions.every((a) => a.direction === INBOUND);
  }

  const journeyOptions = isReturn
    ? [buildJourneyOption(OUTBOUND), buildJourneyOption(INBOUND), buildJourneyOption(BOTH)]
    : isInboundOnly
    ? [buildJourneyOption(INBOUND)]
    : [buildJourneyOption(OUTBOUND)];

  const onHotelFormSubmit = async () => {
    await Promise.allSettled([
      importHotelBooking({
        variables: {
          options: {
            bookingCode: jacTravelRef,
            pnr: bookingReference,
            provider: 'JACTRAVEL',
            product: 'PACKAGES',
            language: 'EN',
            market: 'UK'
          }
        }
      }),
      getHotelBookingInfo({ variables: { externalReference: jacTravelRef, productType: 'PACKAGES' } })
    ]);
  };

  const handleOnSubmit = async (formData) => {
    if (isPackageBooking && !hasActiveHotel && showHotel && hotelInfo) {
      const { data } = await addHotelProduct({
        variables: {
          currency,
          bookingReference,
          newHotelInput: {
            fee: 0,
            margin: hotelInfo.margin,
            price: hotelInfo.isDotW ? hotelInfo.priceBeforeMargin : hotelInfo.price,
            hotelId: formData.hotelId,
            checkInDate: hotelInfo.arrivalDate,
            jacTravelRef: formData.jacTravelRef,
            eurostarRef: formData.eurostarPackageRef
          }
        }
      });

      const itemRefId = parseInt(data?.addHotelProduct?.hotels[0]?.itemRef, 10);
      if (itemRefId) {
        await addAdditionalDetails({
          variables: {
            bookingReference,
            additionalDetails: buildAdditionalDetails({ itemRefId, hotelInfo })
          }
        });
      }

      if (hotelInfo?.isDotW) {
        await addExternalReference({
          variables: {
            reference: bookingReference,
            externalReference: { id: bookingReference, source: EXTERNAL_REFERENCES.EHL }
          }
        });
      }
    }

    if (showExtras) {
      const { hasExceededOutboundLimit, hasExceededInboundLimit } = getExtrasLimitExceededDirections(
        state.formPayload,
        outboundExtras,
        inboundExtras,
        outboundExtrasOptions,
        inboundExtrasOptions
      );

      if (hasExceededOutboundLimit || hasExceededInboundLimit) {
        const getDirectionTotal = (extraOptions = []) => {
          if (extraOptions.length === 0) {
            return 0;
          }

          const option = extraOptions[0];

          if (option.availableUnits !== undefined) {
            return option.availableUnits;
          }

          return Math.floor((option.physicalAvailability || 0) / (option.inventoryUnits || 1));
        };

        setExceededLimits({
          outbound: hasExceededOutboundLimit,
          inbound: hasExceededInboundLimit,
          outboundTotal: getDirectionTotal(outboundExtrasOptions),
          inboundTotal: getDirectionTotal(inboundExtrasOptions)
        });

        return;
      }
    }

    if ((showAssistance || showExtras) && !state.isAssistanceIncomplete) {
      if (!isEmpty(state.formPayload.remove)) {
        await cancelSpecificBookingItems({
          variables: { bookingReference, itemRefs: state.formPayload.remove }
        });

        if (state.formPayload.removedDetails?.length) {
          await deleteAdditionalDetails({
            variables: { bookingReference, additionalDetails: state.formPayload.removedDetails }
          });
        }
      }
      if (!isEmpty(state.formPayload.add)) {
        const input = state.formPayload.add.map((add) => ({
          id: add.id,
          items: add.items.map(({ comment, direction, passengerId }) => ({
            comment,
            direction,
            passengerId
          }))
        }));
        const response = await addAssistanceToBooking({
          variables: { bookingReference, input, applySalesRestrictionOverrides: isEurostarOrganization }
        });
        const { outboundExtras = [], inboundExtras = [] } = response?.data?.addAssistanceToBooking || {};

        const additionalDetails = buildAssistanceAdditionalDetails({
          addedAssistance: state.formPayload.add,
          outboundExtras,
          inboundExtras
        });

        if (additionalDetails.length) {
          await addAdditionalDetails({
            variables: {
              bookingReference,
              additionalDetails
            }
          });
        }
      }
      if (!isEmpty(state.formPayload.update)) {
        const { detailsToAdd, detailsToUpdate } = buildAssistanceUpdatedDetails({ updatedAssistance: state.formPayload.update });

        if (detailsToAdd.length) {
          await addAdditionalDetails({
            variables: {
              bookingReference,
              additionalDetails: detailsToAdd
            }
          });
        }

        if (detailsToUpdate.length) {
          await updateAdditionalDetails({
            variables: {
              bookingReference,
              additionalDetails: detailsToUpdate
            }
          });
        }
      }
    }

    if (shouldConfirmBooking) {
      await confirmBooking({ variables: { bookingReference } });
    }

    if (refetchBooking) {
      await refetchBooking({ reference: bookingReference });
    }

    if (onNext) {
      if (setLoading) {
        setLoading(false);
      }
      if (setIsConfirmDisabled) {
        setIsConfirmDisabled(false);
      }

      onNext();
    }
  };

  useEffect(() => {
    setLoading(
      [
        addHotelLoading,
        addAssistanceLoading,
        removeAssistanceLoading,
        confirmBookingLoading,
        addAdditionalDetailsLoading,
        updateAdditionalDetailsLoading,
        addExternalReferenceLoading
      ].some(Boolean)
    );
  }, [
    addHotelLoading,
    addAssistanceLoading,
    removeAssistanceLoading,
    setLoading,
    confirmBookingLoading,
    addAdditionalDetailsLoading,
    updateAdditionalDetailsLoading,
    addExternalReferenceLoading
  ]);

  useEffect(() => {
    const isConfirmDisabled =
      confirmBookingLoading ||
      addAssistanceLoading ||
      removeAssistanceLoading ||
      addHotelLoading ||
      addAdditionalDetailsLoading ||
      updateAdditionalDetailsLoading ||
      deleteAdditionalDetailsLoading ||
      addExternalReferenceLoading ||
      isGeneralWarningVisible ||
      isHotelFormInvalid;

    setIsConfirmDisabled(
      isConfirmDisabled ||
        (!state.formPayload.add.length && !state.formPayload.update?.length && !state.formPayload.remove.length && !isPackageBooking)
    );
  }, [
    addAssistanceLoading,
    removeAssistanceLoading,
    addHotelLoading,
    addAdditionalDetailsLoading,
    updateAdditionalDetailsLoading,
    deleteAdditionalDetailsLoading,
    addExternalReferenceLoading,
    isGeneralWarningVisible,
    isHotelFormInvalid,
    state.formPayload,
    isPackageBooking,
    setIsConfirmDisabled,
    confirmBookingLoading
  ]);

  useEffect(() => {
    if (hasDogPassenger && !outboundExtras.some((extra) => extra.code === ASSISTANCE_KEYS.GUIDE_DOG_ASSIST)) {
      dispatch(onAddItemAction(ASSISTANCE_KEYS.GUIDE_DOG_ASSIST));
    }
  }, [hasDogPassenger, outboundExtras, showAssistance]);

  useEffect(() => {
    if (exceededLimits.outbound || exceededLimits.inbound) {
      setExceededLimits({
        outbound: false,
        inbound: false,
        outboundTotal: 0,
        inboundTotal: 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.formPayload.add, state.formPayload.remove]);

  return (
    <form id="add-assistance-form" data-testid="add-assistance-form" onSubmit={handleSubmit(handleOnSubmit)}>
      {showHotel && isPackageBooking && !hasActiveHotel && (
        <StyledHeader>
          <h1>Hotel</h1>
          <AddHotelForm
            hasErrors={hasErrors}
            errors={errors}
            error={hotelFormError}
            register={register}
            values={{ jacTravelRef }}
            onSubmit={onHotelFormSubmit}
            hotelData={hotelInfo}
            hotelDataLoading={hotelLoading || hotelBookingLoading || importHotelBookingLoading}
          />
        </StyledHeader>
      )}

      {showExtras && isBookingPathExtrasEnabled && (
        <StyledHeader>
          <h1>
            <FormattedMessage id="addExtras.title" data-testid="addAssistance.title" />
          </h1>
          <Container data-cy="add-extras">
            <h3>
              <FormattedMessage id="addAssistance.journey.outbound" />
            </h3>
            {outboundExtrasOptions.length > 0 ? (
              outboundExtrasOptions.map((extra) => {
                const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[extra.id];

                return (
                  <AddAssistanceOptions
                    currency={currency}
                    key={extra.id}
                    isIncomplete={isIncomplete}
                    assistance={extra}
                    isExtra
                    isGroupBooking={isGroupBooking}
                    directions={[buildJourneyOption(extra.direction)]}
                    passengers={passengers}
                    isAddPassengersDisabled={isAddPassengersDisabled}
                    isItemsVisible={isVisible}
                    items={items}
                    onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(extra.id))}
                    onAddItem={() => dispatch(onAddItemAction(extra.id))}
                    onChangeItem={(item) => dispatch(onEditItemAction({ key: extra.id, item }))}
                    onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(extra.id))}
                    onRemoveItem={(item) =>
                      dispatch(
                        onRemoveItemAction({
                          key: extra.id,
                          item
                        })
                      )
                    }
                  />
                );
              })
            ) : (
              <StyledMessage>
                <FormattedMessage id="addExtras.noAvailability" />
              </StyledMessage>
            )}
            {isReturn && (
              <>
                <h3>
                  <FormattedMessage id="addAssistance.journey.inbound" />
                </h3>
                {inboundExtrasOptions.length > 0 ? (
                  inboundExtrasOptions.map((extra) => {
                    const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[extra.id];

                    return (
                      <AddAssistanceOptions
                        currency={currency}
                        key={extra.id}
                        isIncomplete={isIncomplete}
                        assistance={extra}
                        isExtra
                        isGroupBooking={isGroupBooking}
                        directions={[buildJourneyOption(extra.direction)]}
                        passengers={passengers}
                        isAddPassengersDisabled={isAddPassengersDisabled}
                        isItemsVisible={isVisible}
                        items={items}
                        onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(extra.id))}
                        onAddItem={() => dispatch(onAddItemAction(extra.id))}
                        onChangeItem={(item) => dispatch(onEditItemAction({ key: extra.id, item }))}
                        onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(extra.id))}
                        onRemoveItem={(item) =>
                          dispatch(
                            onRemoveItemAction({
                              key: extra.id,
                              item
                            })
                          )
                        }
                      />
                    );
                  })
                ) : (
                  <StyledMessage>
                    <FormattedMessage id="addExtras.noAvailability" />
                  </StyledMessage>
                )}
              </>
            )}

            {isGeneralWarningVisible && (
              <StyledGenericMessage data-cy="add-assistance-warning" data-testid="add-assistance-warning">
                <FormattedMessage id="addAssistance.generalWarning" />
              </StyledGenericMessage>
            )}
            {exceededLimits.outbound && (
              <StyledGenericMessage data-cy="add-extras-warning" data-testid="add-extras-warning">
                <FormattedMessage id="addExtras.outboundTotalExtras" values={{ amount: exceededLimits.outboundTotal }} />
              </StyledGenericMessage>
            )}
            {exceededLimits.inbound && (
              <StyledGenericMessage data-cy="add-extras-warning" data-testid="add-extras-warning">
                <FormattedMessage id="addExtras.inboundTotalExtras" values={{ amount: exceededLimits.inboundTotal }} />
              </StyledGenericMessage>
            )}
          </Container>
        </StyledHeader>
      )}

      {showAssistance && (
        <StyledHeader>
          <h1>
            <FormattedMessage id="addAssistance.title" data-testid="addAssistance.title" />
          </h1>
          <Container data-cy="add-assistance">
            {assistanceOptions.length > 0 ? (
              <>
                <h2 data-testid="addAssistance.paragraph" style={{ fontSize: 16 }}>
                  <FormattedMessage id="addAssistance.paragraph" />
                </h2>
                {sortedAssistanceOptions.map((assistanceOption) => {
                  const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[assistanceOption.id];
                  return (
                    <AddAssistanceOptions
                      currency={currency}
                      key={assistanceOption.id}
                      isIncomplete={isIncomplete}
                      assistance={assistanceOption}
                      isExtra={false}
                      isGroupBooking={isGroupBooking}
                      directions={journeyOptions}
                      passengers={passengers}
                      isAddPassengersDisabled={isAddPassengersDisabled}
                      isItemsVisible={isVisible}
                      items={items}
                      onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(assistanceOption.id))}
                      onAddItem={() => dispatch(onAddItemAction(assistanceOption.id))}
                      onChangeItem={(item) => dispatch(onEditItemAction({ key: assistanceOption.id, item }))}
                      onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(assistanceOption.id))}
                      onRemoveItem={(item) =>
                        dispatch(
                          onRemoveItemAction({
                            key: assistanceOption.id,
                            item
                          })
                        )
                      }
                    />
                  );
                })}
                {isGeneralWarningVisible && (
                  <StyledGenericMessage data-cy="add-assistance-warning" data-testid="add-assistance-warning">
                    <FormattedMessage id="addAssistance.generalWarning" />
                  </StyledGenericMessage>
                )}
              </>
            ) : (
              <NoAssistanceResults />
            )}
          </Container>
        </StyledHeader>
      )}
    </form>
  );
};

AddAssistanceForm.propTypes = {
  assistanceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      passengerTypes: PropTypes.arrayOf(PropTypes.string),
      passengers: PropTypes.arrayOf(PropTypes.string),
      direction: PropTypes.string.isRequired,
      price: PropTypes.number
    })
  ),
  outboundExtrasOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      passengerTypes: PropTypes.arrayOf(PropTypes.string),
      passengers: PropTypes.arrayOf(PropTypes.string),
      direction: PropTypes.string.isRequired,
      price: PropTypes.number,
      physicalAvailability: PropTypes.number,
      inventoryUnits: PropTypes.number,
      availableUnits: PropTypes.number.isRequired
    })
  ),
  inboundExtrasOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      passengerTypes: PropTypes.arrayOf(PropTypes.string),
      passengers: PropTypes.arrayOf(PropTypes.string),
      direction: PropTypes.string.isRequired,
      price: PropTypes.number,
      physicalAvailability: PropTypes.number,
      inventoryUnits: PropTypes.number,
      availableUnits: PropTypes.number.isRequired
    })
  ),
  bookingReference: PropTypes.string.isRequired,
  refetchBooking: PropTypes.func,
  isReturn: PropTypes.bool.isRequired,
  onNext: PropTypes.func.isRequired,
  passengers: passengersShape,
  initialState: PropTypes.shape({}),
  journeyDigest: PropTypes.shape({
    inboundShape: PropTypes.arrayOf(legShape),
    outboundShape: PropTypes.arrayOf(legShape),
    offlineFee: feeShape,
    totalPrice: PropTypes.number,
    currency: PropTypes.string,
    inboundTotalPrice: PropTypes.number,
    outboundTotalPrice: PropTypes.number
  }),
  currency: PropTypes.string.isRequired,
  isPackageBooking: PropTypes.bool.isRequired,
  hasActiveHotel: PropTypes.bool,
  setLoading: PropTypes.func,
  setIsConfirmDisabled: PropTypes.func,
  shouldConfirmBooking: PropTypes.bool,
  outboundExtras: PropTypes.arrayOf(legShape),
  inboundExtras: PropTypes.arrayOf(legShape),
  isDotwBooking: PropTypes.bool,
  isEurostarOrganization: PropTypes.bool,
  isGroupBooking: PropTypes.bool,
  showAssistance: PropTypes.bool,
  showExtras: PropTypes.bool,
  showHotel: PropTypes.bool
};

export default AddAssistanceForm;
